import { Divider, Table, TableProps } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { CustomTitle } from '@/components';

interface ProductRow {
  key?: string;
  name?: string;

  export_value?: boolean | number | string | null;
  import_value?: boolean | number | string | null;
  transit_value?: boolean | number | string | null;
  is_grouped?: boolean | number | string | null;
  value?: boolean | number | string | null;

  product?: string;
  export?: boolean | number | string | null;
  import?: boolean | number | string | null;
  transit?: boolean | number | string | null;

  isSection?: boolean;
}

export interface Capability {
  name: string;
  availability: boolean | null;
  remarks: string | null;
  value: string | number | boolean | null;
  export_value: string | number | boolean | null;
  import_value: string | number | boolean | null;
  transit_value: string | number | boolean | null;
}

export interface ProductGroup {
  name?: string;
  type?: 'Speeds' | 'Products';
  capabilities?: Capability[];
}

export interface LufthansaCapabilitiesProps {
  products?: ProductGroup[];
}

export const normalizeValue = (
  val: boolean | number | string | null | undefined
): boolean | number | string | null | undefined => {
  if (val === true || val === 'True') return true;
  if (val === false || val === 'False') return false;
  return val;
};
const LufthansaCapabilities = ({ products }: LufthansaCapabilitiesProps) => {
  // Normalizes boolean-like string or boolean

  const getColumns = (
    firstColumnTitle: string
  ): TableProps<ProductRow>['columns'] => [
    {
      title: firstColumnTitle,
      dataIndex: 'product',
      key: 'product',
      width: '55%',
      render: (_: unknown, record: ProductRow) => {
        // Section row (your existing condition)
        if (record.name == 'General remarks') {
          return {
            children: <span>{record.value}</span>,
            props: { colSpan: 4 },
          };
        }

        return record.name;
      },
    },

    // EXPORT
    {
      title: 'Export',
      dataIndex: 'export',
      key: 'export',
      width: '15%',
      render: (_: unknown, record: ProductRow) => {
        if (record.name == 'General remarks') {
          return { props: { colSpan: 0 } };
        }

        if (record?.is_grouped) {
          const mergedVal = normalizeValue(record.value);
          return {
            children: mergedVal === true ? <CheckOutlined /> : mergedVal,
            props: { colSpan: 4 },
          };
        }

        const val = normalizeValue(record.export_value);
        return val === true ? <CheckOutlined /> : val || '-';
      },
    },

    // IMPORT
    {
      title: 'Import',
      dataIndex: 'import',
      key: 'import',
      width: '15%',
      render: (_: unknown, record: ProductRow) => {
        if (record.is_grouped || record.isSection) {
          return { props: { colSpan: 0 } };
        }

        const val = normalizeValue(record.import_value);
        return val === true ? <CheckOutlined /> : val || '-';
      },
    },

    // TRANSIT
    {
      title: 'Transit',
      dataIndex: 'transit',
      key: 'transit',
      width: '15%',
      render: (_: unknown, record: ProductRow) => {
        if (record.is_grouped || record.isSection) {
          return { props: { colSpan: 0 } };
        }

        const val = normalizeValue(record.transit_value);
        return val === true ? <CheckOutlined /> : val || '-';
      },
    },
  ];

  const safeProducts = products ?? [];

  return (
    <div>
      {[...safeProducts].reverse().map((product, index) => {
        const isSpeeds = product.type === 'Speeds';
        return (
          <div key={index}>
            {isSpeeds && (
              <CustomTitle
                text='Speed'
                color='var(--text-primary)'
                fontSize='16px'
                lineHeight='20px'
                fontWeight='600'
                marginBottom={12}
              />
            )}

            <Table<ProductRow>
              className='zl-custom-times-table'
              dataSource={product.capabilities as ProductRow[] | undefined}
              columns={getColumns(isSpeeds ? '' : (product.name ?? ''))}
              pagination={false}
              rowHoverable={false}
              bordered
              rowClassName={(record) => {
                if (
                  record.name === 'General remarks' &&
                  (record.value === null ||
                    record.value === '' ||
                    record.value === undefined ||
                    record.value === 'False')
                ) {
                  return 'antd-row-row-hide';
                }

                if (record.name === 'General remarks') {
                  return 'cell-bg-gray';
                }

                return '';
              }}
            />

            {index !== safeProducts.length - 1 && <Divider />}
          </div>
        );
      })}
    </div>
  );
};

export default LufthansaCapabilities;
